import {AutogenLayoutViewMovementsMap} from "./autogen/AutogenLayoutViewMovementsMap";
import {DataDisplayEvent, DistanceProfileService, MapPin, MapVendor, PinPlotEvent, RouteType, Snackbar} from "@mcleod/components";
import { DesignableObjectLogManager } from "@mcleod/components/src/logging/DesignableObjectLogManager";
import {Api, DateUtil, MapSettings, StringUtil} from "@mcleod/core";
import {LMEUserSettings} from "@mcleod/general";

const log = DesignableObjectLogManager.getLogger("components.map");

// Define constants for vendor identification
const PCMILER_ON_PREM = 'A';
const PCMILER_WEB = 'W';       // Used in MovementMap.ts
const PCMILER_WEB_ALT = 'T';   // Used in DistanceRateIndexCalculator.ts
const MILEMAKER = 'E';

const getDefaultRouteConfig = () => ({
    showGoogleRoute: false,
    showGoogleToggle: false,
    showMileMakerRoute: LMEUserSettings.get()?.show_proposed_route === "Y",
    showMileMakerToggle: true,
    showTrimbleRoute: LMEUserSettings.get()?.show_proposed_route === "Y",
    showTrimbleToggle: true
});

export class ViewMovementsMap extends AutogenLayoutViewMovementsMap {
    private _route: any[];
    private routeConfig = this.getConfigWithUserSettings();
    private isPcMilerOnPremVendor: boolean = false;
    private isPcMilerWebVendor: boolean = false;
    private isMileMakerVendor: boolean = false;

    override onLoad(): void | Promise<void> {
        this.mapPositions.addPinPlotListener(this.formatMapPin);

        this.mapPositions.addDataDisplayListener(async (event: DataDisplayEvent) => {
            log.debug("ViewMovementsMap: Map data display event fired, but waiting for route data");
            // Don't automatically create route here - let addPinsForMovement handle it
        });
    }

    private getConfigWithUserSettings() {
        // Get base config from MapSettings
        const baseConfig = MapSettings.getSingleton().getRouteConfig();

        // Apply user settings
        const showProposedRoute = LMEUserSettings.get()?.show_proposed_route === "Y";

        return {
            ...baseConfig,
            // Override route visibility with user preference
            showMileMakerRoute: showProposedRoute,
            showTrimbleRoute: showProposedRoute
        };
    }

    configureRoutes(config: Partial<typeof MapSettings.DEFAULT_ROUTE_CONFIG>): void {
        // Get fresh defaults including user settings
        this.routeConfig = {
            ...this.getConfigWithUserSettings(),
            ...config
        };
        log.debug("ViewMovementsMap: Route configuration updated", this.routeConfig);
    }

    public addPinsForMovement(movement_id: string) {
        if (this.mapPositions.vendor === MapVendor.NONE) return;

        this.mainDataSource?.search({movement_id}).then((result) => {
            const stops = result.modelRows;
            this._route = stops.map((row, idx, arr) => {
                let data_type = "S";
                let stop_label = `Stop ${idx + 1}/${arr.length}:`;
                if (idx === 0) {
                    data_type = "O";
                    stop_label = "Shipper:";
                } else if (idx === arr.length - 1) {
                    data_type = "D";
                    stop_label = "Consignee:";
                }
                return {
                    data_type,
                    stop_label,
                    order_sequence: row.get("order_sequence"),
                    latitude: row.get("latitude"),
                    longitude: row.get("longitude"),
                    city_id: row.get("city_id"),
                    city_name: row.get("city_name"),
                    state: row.get("state"),
                    zip_code: row.get("zip_code"),
                    movement_sequence: row.get("movement_sequence"),
                    status: row.get("status"),
                    stop_type_value: row.get("stop_type_value"),
                    stop_status_info: row.get("stop_status_info"),
                    stop_location: row.get("stop_location"),
                    stop_date_info: row.get("stop_date_info"),
                };
            });
            // Only create route if we actually have data
            if (this._route && this._route.length > 0) {
                // If map is already displayed, create route now
                if (this.mapPositions.map) {
                    log.debug("ViewMovementsMap: Map already available, creating route");
                    this.createRouteForVendor(this._route);
                } else {
                    // Otherwise wait for map to be ready
                    log.debug("ViewMovementsMap: Map not yet available, waiting for data display event");
                    const originalListener = (event: DataDisplayEvent) => {
                        log.debug("ViewMovementsMap: Map now available, creating route");
                        this.createRouteForVendor(this._route);
                        // Remove the reference to prevent memory leaks
                        this.mapPositions.addDataDisplayListener = this.mapPositions.addDataDisplayListener.bind(this.mapPositions);
                    };
                    this.mapPositions.addDataDisplayListener(originalListener);
                }
            } else {
                log.error("ViewMovementsMap: No route data available after search");
            }

            this.mapPositions.focus();
        }).catch((error) => {
            log.error("Error placing movement pins:", error);
        });
    }

    private async createRouteForVendor(routeData: any[]): Promise<void> {
        // Add a guard clause at the beginning
        if (!routeData || routeData.length === 0) {
            log.error("ViewMovementsMap: createRouteForVendor called with no route data");
            return;
        }

        log.debug('ViewMovementsMap: Starting createRouteForVendor with route data length:', routeData?.length);

        // Use route config instead of user settings
        const showGoogleRoute = this.routeConfig.showGoogleRoute;
        const showProposedRoute = this.routeConfig.showMileMakerRoute;
        const showTrimbleRoute = this.routeConfig.showTrimbleRoute;
        const showGoogleToggle = this.routeConfig.showGoogleToggle;
        const showMileMakerToggle = this.routeConfig.showMileMakerToggle;
        const showTrimbleToggle = this.routeConfig.showTrimbleToggle;

        const vendor = this.mapPositions.vendor;

        log.debug('ViewMovementsMap: Route settings:', {
            showGoogleRoute,
            showProposedRoute,
            showTrimbleRoute,
            mapVendor: vendor,
            vendorName: MapVendor[vendor],
            routeConfig: this.routeConfig
        });

        // Get distance profile info to determine routing vendor
        let vendorInfo = null;

        try {
            log.debug('ViewMovementsMap: Fetching distance profile info...');
            vendorInfo = await DistanceProfileService.getDistanceVendorInfo("fuel_dist_profile");

            this.isMileMakerVendor = vendorInfo.isMileMaker === true;
            this.isPcMilerOnPremVendor = vendorInfo.isPcMiler === true;
            this.isPcMilerWebVendor = vendorInfo.isPcMiler === true &&
                (vendorInfo.source === PCMILER_WEB || vendorInfo.source === PCMILER_WEB_ALT);

            log.debug('ViewMovementsMap: Distance vendor info:', {
                vendorInfo,
                isMileMakerVendor: this.isMileMakerVendor,
                isPcMilerOnPremVendor: this.isPcMilerOnPremVendor,
                isPcMilerWebVendor: this.isPcMilerWebVendor
            });
        } catch (error) {
            log.error("ViewMovementsMap: Error fetching distance vendor info:", error);
        }

        if (vendor === MapVendor.GOOGLE) {
            // Don't show PC*Miler Web route on Google maps
            const adjustedShowTrimbleToggle = this.isPcMilerWebVendor ? false : showTrimbleToggle;

            // Get MileMaker route data if showing proposed route
            let milemakerRoute = [];
            let trimbleRoute = [];

            if (showProposedRoute) {
                try {
                    const response = await Api.search("lme/dispatch/distance-lookup", {
                        stops: routeData,
                        dist_profile: "fuel_dist_profile"
                    });

                    milemakerRoute = Array.isArray(response.data) && response.data.length > 0
                        ? response.data[0].milemaker_route || []
                        : [];

                    trimbleRoute = Array.isArray(response.data) && response.data.length > 0
                        ? response.data[0].trimble_route || []
                        : [];

                    log.debug('ViewMovementsMap: Retrieved route data', {
                        responseHasData: Array.isArray(response.data) && response.data.length > 0,
                        milemakerRouteLength: milemakerRoute.length,
                        trimbleRouteLength: trimbleRoute.length
                    });
                } catch (error) {
                    log.error('ViewMovementsMap: Error fetching route data', error);
                }
            }

            // Create vendor info object with route config
            const vendorInfoObj = {
                isMileMaker: this.isMileMakerVendor,
                isPcMiler: this.isPcMilerOnPremVendor,
                isPcMilerWeb: this.isPcMilerWebVendor,
                routeConfig: {
                    showGoogleToggle,
                    showMileMakerToggle,
                    showTrimbleToggle: adjustedShowTrimbleToggle
                }
            };

            // Create route ID with vendor info
            const googleRouteId = JSON.stringify({
                routeId: "googleRoute",
                vendorInfo: vendorInfoObj
            });

            const milemakerRouteId = JSON.stringify({
                routeId: "milemakerRoute",
                vendorInfo: vendorInfoObj
            });

            // Create route ID for PC*Miler/Trimble
            const trimbleRouteId = JSON.stringify({
                routeId: "trimbleRoute",
                vendorInfo: vendorInfoObj
            });

            this.mapPositions.createRoute(
                routeData,
                RouteType.Google,
                undefined,
                googleRouteId,
                undefined,
                false,
                false
            );

            // Handle MileMaker route
            if (showProposedRoute && milemakerRoute.length > 0 && this.isMileMakerVendor) {
                log.debug('ViewMovementsMap: Creating MileMaker route with route data:', {
                    milemakerRouteLength: milemakerRoute.length,
                    showProposedRoute,
                    isMileMakerVendor: this.isMileMakerVendor
                });

                this.mapPositions.createRoute(
                    milemakerRoute,
                    RouteType.MileMaker,
                    undefined,           // hideMarkers
                    milemakerRouteId,    // routeId with vendor info
                    undefined,           // routeColor
                    showProposedRoute,   // showProposedRoute
                    false                // disableToggleButtons
                );
            } else if (this.isMileMakerVendor && showMileMakerToggle) {
                // Create an "empty" MileMaker route just to pass vendor info
                // This ensures the toggle is created even when the route is hidden
                log.debug('ViewMovementsMap: Creating empty MileMaker route to show toggle', {
                    isMileMakerVendor: this.isMileMakerVendor,
                    showMileMakerToggle
                });

                this.mapPositions.createRoute(
                    routeData,             // Use actual route data instead of []
                    RouteType.MileMaker,
                    true,                  // hideMarkers
                    milemakerRouteId,      // routeId with vendor info
                    undefined,             // routeColor
                    showProposedRoute,     // showProposedRoute (will be false)
                    false                  // disableToggleButtons
                );
            }

            // Handle PC*Miler/Trimble route
            log.debug('ViewMovementsMap: PC*Miler route creation check', {
                showProposedRoute,
                showTrimbleRoute,
                trimbleRouteLength: trimbleRoute?.length || 0,
                isPcMilerOnPremVendor: this.isPcMilerOnPremVendor,
                isPcMilerWebVendor: this.isPcMilerWebVendor,
                shouldCreateRoute: showProposedRoute &&
                                  trimbleRoute?.length > 0 &&
                                  this.isPcMilerOnPremVendor &&
                                  !this.isPcMilerWebVendor &&
                                  showTrimbleRoute
            });

            if (showProposedRoute && trimbleRoute.length > 0 &&
                this.isPcMilerOnPremVendor && !this.isPcMilerWebVendor &&
                showTrimbleRoute) {
                // Create PC*Miler route with actual route data
                log.debug('ViewMovementsMap: Creating PC*Miler route with actual data', {
                    trimbleRouteLength: trimbleRoute.length,
                    showProposedRoute,
                    showTrimbleRoute
                });

                this.mapPositions.createRoute(
                    trimbleRoute,
                    RouteType.Trimble,
                    undefined,           // hideMarkers
                    trimbleRouteId,      // routeId with vendor info
                    undefined,           // routeColor
                    showTrimbleRoute,    // showProposedRoute
                    false                // disableToggleButtons
                );
            } else if (this.isPcMilerOnPremVendor && !this.isPcMilerWebVendor && adjustedShowTrimbleToggle) {
                // Create an "empty" PC*Miler route just to pass vendor info
                // This ensures the toggle is created even when the route is hidden
                log.debug('ViewMovementsMap: Creating empty PC*Miler route to show toggle', {
                    isPcMilerOnPremVendor: this.isPcMilerOnPremVendor,
                    isPcMilerWebVendor: this.isPcMilerWebVendor,
                    adjustedShowTrimbleToggle
                });

                this.mapPositions.createRoute(
                    routeData,           // Empty route data
                    RouteType.Trimble,
                    true,                // hideMarkers
                    trimbleRouteId,      // routeId with vendor info
                    undefined,           // routeColor
                    showTrimbleRoute,    // showProposedRoute (will be false)
                    false                // disableToggleButtons
                );
            }
        } else if (vendor === MapVendor.TRIMBLE) {
            // For Trimble maps, create the route directly - don't wait for style load in this method
            log.debug("ViewMovementsMap: Starting Trimble route creation");

            // Instead of waiting here, always call createTrimbleRoute
            // The style check will happen inside createTrimbleRoute
            this.createTrimbleRoute(routeData);
        }
    }

    private createTrimbleRoute(routeData: any[]): void {
        // Check if map is initialized and style is loaded
        if (!this.mapPositions.map) {
            log.error("ViewMovementsMap: Map not initialized in createTrimbleRoute");
            return;
        }

        // Check if style is loaded, if not, wait for it
        if (!this.mapPositions.map.isStyleLoaded()) {
            log.debug("ViewMovementsMap: Trimble map style not loaded, waiting...");

            // Set up a retry mechanism with setTimeout
            setTimeout(() => {
                log.debug("ViewMovementsMap: Retrying Trimble route creation");
                this.createTrimbleRoute(routeData);
            }, 500); // Try again after 500ms
            return;
        }

        log.debug("ViewMovementsMap: Trimble map style is loaded, proceeding with route creation");

        // For Trimble maps, we need to draw Trimble routes with coordinate arrays if available
        if ((this.isPcMilerOnPremVendor || this.isPcMilerWebVendor) && routeData) {
            try {
                // Get route data from API
                Api.search("lme/dispatch/distance-lookup", {
                    stops: routeData,
                    dist_profile: "fuel_dist_profile"
                }).then(response => {
                    log.debug("ViewMovementsMap: Retrieved Trimble route data", {
                        responseStatus: response?.status,
                        hasTrimbleRoute: Boolean(response?.data[0]?.trimble_route),
                        hasStopCoords: Boolean(response?.data[0]?.route_stop_coords)
                    });

                    if (response?.data?.length > 0) {
                        // Create a properly structured route object
                        const routeDataWithCoordinates = {
                            ...routeData,
                            trimble_coordinate_data: response.data[0].trimble_route || [],
                            route_stop_coords: response.data[0].route_stop_coords || []
                        };

                        const hideMarkers = true;
                        const removeAllPins = false;

                        // Use the coordinate array if available, otherwise use default route creation
                        if (routeDataWithCoordinates.route_stop_coords?.length > 0) {
                            log.debug("ViewMovementsMap: Using coordinate array for Trimble route");
                            this.mapPositions.createRouteFromCoordArray(
                                routeDataWithCoordinates,
                                RouteType.Trimble,
                                hideMarkers,
                                removeAllPins
                            );
                        } else {
                            log.debug("ViewMovementsMap: No coordinate array, using stop coordinates for Trimble route");
                            this.mapPositions.createRoute(routeData, RouteType.Trimble, hideMarkers);
                        }
                    } else {
                        log.error("ViewMovementsMap: No route data returned from distance lookup");
                        this.mapPositions.createRoute(routeData, RouteType.Trimble, true);
                    }
                }).catch(error => {
                    log.error("ViewMovementsMap: Error creating Trimble route:", error);
                    // Fallback to basic route without coordinates
                    this.mapPositions.createRoute(routeData, RouteType.Trimble, true);
                });
            } catch (error) {
                log.error("ViewMovementsMap: Error creating Trimble route:", error);
                // Fallback to basic route without coordinates
                this.mapPositions.createRoute(routeData, RouteType.Trimble, true);
            }
        } else {
            // Not using PC*Miler, just draw a simple route
            log.debug("ViewMovementsMap: Creating basic route for Trimble map (not PC*Miler)");

            // Clear any existing routes before creating a new one
            this.mapPositions.clearRoute();

            // Now create the route
            this.mapPositions.createRoute(routeData, RouteType.Trimble, true);
        }
    }

    private formatMapPin(event: PinPlotEvent) {
        log.debug('ViewMovementsMap: Formatting map pin:', event);

        const pin: MapPin = event.pin;
        log.debug('ViewMovementsMap: Pin object:', {
            latitude: pin.latitude,
            longitude: pin.longitude,
            caption: pin.caption,
            captionColor: pin.captionColor,
            image: pin.image,
            tooltip: pin.tooltip,
            clusterable: pin.clusterable,
            data: pin.data ? 'exists' : 'missing'
        });

        if (!pin.data) {
            log.error('ViewMovementsMap: Pin data is null or undefined');
            return;
        }

        // Log all pin data fields for debugging
        log.debug('ViewMovementsMap: Pin data fields:', {
            data_type: pin.data.get("data_type"),
            stop_label: pin.data.get("stop_label"),
            name: pin.data.get("name"),
            status: pin.data.get("status"),
            dates: pin.data.get("dates"),
            dates_tz: pin.data.get("dates_tz"),
            dist_previous: pin.data.get("dist_previous"),
            type: pin.data.get("type")
        });

        const dataType = pin.data.get("data_type");
        const datesString: string = pin.data.get("dates");
        const dates: Date = datesString ? new Date(datesString) : null;
        let formattedDates: string = '';

        try {
            if (dates != null) {
                formattedDates = DateUtil.formatDateTime(dates, DateUtil.getUserDateTimeFormat());
                log.debug('ViewMovementsMap: Formatted dates:', formattedDates);
            } else {
                formattedDates = datesString || '';
                log.debug('ViewMovementsMap: Using original date string:', formattedDates);
            }
        } catch (error) {
            log.error('ViewMovementsMap: Error formatting dates:', error);
            formattedDates = '';
        }

        switch (dataType) {
            case "O":
                pin.image = "map-pin-location";
                pin.tooltip = ViewMovementsMap.buildStopTooltip(pin, formattedDates);
                break;
            case "D":
                pin.image = "map-pin-location";
                pin.tooltip = ViewMovementsMap.buildStopTooltip(pin, formattedDates);
                break;
            case "S":
                pin.image = "map-pin-location";
                pin.tooltip = ViewMovementsMap.buildStopTooltip(pin, formattedDates);
                break;
            case "P":
                pin.image = "map-pin-location-blue";
                pin.tooltip = `Mobile Comm. Position\nLatitude: ${pin.latitude}\nLongitude: ${pin.longitude}\nDates: ${formattedDates}`;
                break;
            default:
                throw new Error(`Unknown data type: ${dataType}`);
        }

        pin.clusterable = false;
    }

    private static buildStopTooltip(pin: MapPin, formattedDates: string): string {
        log.debug('ViewMovementsMap: Building stop tooltip', {
            pinData: pin.data ? 'exists' : 'missing',
            formattedDates
        });

        if (!pin.data) {
            log.error('ViewMovementsMap: Pin data is null or undefined in buildStopTooltip');
            return "No stop data available";
        }

        try {
            const distPrevious = pin.data.get("dist_previous", null);
            let distPreviousAddition = "";
            if (distPrevious) {
                distPreviousAddition = `\nDist Previous: ${distPrevious}`;
            }

            let dates = "";
            if (!StringUtil.isEmptyString(formattedDates)) {
                dates = `\nDates: ${formattedDates} ${pin.data.get("dates_tz") || ''}`;
            }

            const stopLabel = pin.data.get("stop_label") || '';
            const type = pin.data.get("type") || '';
            const status = pin.data.get("status") || '';
            const name = pin.data.get("name") || '';

            const tooltip = `${stopLabel} ${type}, Status: ${status}\nName: ${name}${dates}${distPreviousAddition} `;
            log.debug('ViewMovementsMap: Created tooltip:', tooltip);
            return tooltip;
        } catch (error) {
            log.error('ViewMovementsMap: Error building tooltip:', error);
            return "Error building tooltip";
        }
    }
}
